import { useEffect, useState } from 'react'
import PostReactions from './PostReactions'
import { deletePost } from '../../api'
import { colors } from '../../colors'
import officialMark from '../../images/official_mark.png'
import type { BargainUserInfo, Post } from '../../types'

interface PostCardProps {
  post: Post
  bargain_user_info: BargainUserInfo
  onDelete?: (postId: number) => void
  onReactionChange?: () => void
}

// ポストカード固有のスタイル
const styles: Record<string, React.CSSProperties> = {
  imageCard: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    aspectRatio: '3 / 4',
    cursor: 'pointer',
  },
  imageCardBackground: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imageCardTopOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)',
    padding: '8px',
    color: colors.white,
  },
  imageCardBottomOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
    padding: '8px',
    color: colors.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  imageCardShopInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  imageCardLogoWrapper: {
    width: '32px',
    height: '32px',
    backgroundColor: colors.white,
    border: `1px solid #00000018`,
    borderRadius: '50%',
    padding: '4px',
    marginRight: '6px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageCardLogo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
  },
  imageCardShopName: {
    fontSize: '12px',
    fontWeight: '600',
    color: colors.white,
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  imageCardProductName: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: '4px',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
    lineHeight: '1.4',
  },
  imageCardUserInfo: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '10px',
    color: colors.white,
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  imageCardOfficialMark: {
    width: '12px',
    height: '12px',
    marginLeft: '2px',
  },
  imageCardDeleteButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    fontSize: '11px',
    padding: '4px 8px',
    backgroundColor: 'rgba(0, 0, 0, 0.74)',
    color: colors.white,
    borderRadius: '4px',
    cursor: 'pointer',
    border: 'none',
  },
  // 画像なし投稿用のスタイル
  noImageCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '8px',
    minHeight: '100%',
    border: `1px solid #00000018`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  noImageCardShopName: {
    fontSize: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
  },
  noImageCardProductName: {
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.4',
  },
  noImageCardBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    width: '100%',
  },
  noImageCardDescription: {
    fontSize: '12px',
    color: colors['text-secondary'],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.4',
    marginBottom: '8px',
  },
}

const getUserName = (post: Post) => {
  return post.poster_info?.name || '匿名ユーザー'
}

const getIsStaff = (post: Post) => {
  return post.poster_info?.is_tokubai_staff_post || false
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  bargain_user_info,
  onDelete,
  onReactionChange,
}) => {
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null)
  const handleImageClick = (postId: number) => {
    const urlParams = new URLSearchParams(window.location.search)

    // テーマ投稿一覧ページ（/themes/:id/posts）からの遷移の場合、パスからtheme_idを抽出
    const themeMatch = window.location.pathname.match(/\/themes\/(\d+)\/posts/)
    if (themeMatch && !urlParams.has('theme_id')) {
      urlParams.set('theme_id', themeMatch[1])
    }

    const queryString = urlParams.toString()
    window.location.href = `/webview/message_board/posts/${postId}${queryString ? `?${queryString}` : ''}`
  }

  const handleDelete = async () => {
    if (window.confirm('この投稿を削除してもよろしいですか？')) {
      try {
        await deletePost(post.id)
        if (onDelete) {
          onDelete(post.id)
        }
      } catch {
        alert('投稿の削除に失敗しました。')
      }
    }
  }

  // 画像のアスペクト比を取得
  useEffect(() => {
    if (post.images && post.images.length > 0) {
      const img = new Image()
      img.onload = () => {
        const aspectRatio = img.width / img.height
        setImageAspectRatio(aspectRatio)
      }
      img.src = post.images[0].image_url
    }
  }, [post.images])

  const hasImages = post.images && post.images.length > 0

  const getCardAspectRatio = (): string => {
    if (!hasImages) {
      return '1 / 1' // 正方形（画像なし）
    }
    if (imageAspectRatio === null) {
      return '3 / 4' // デフォルト（読み込み中）
    }
    // 画像のアスペクト比が0.85以上なら正方形、それ以外は縦長
    return imageAspectRatio >= 0.85 ? '1 / 1' : '3 / 4'
  }

  if (hasImages) {
    const firstImage = post.images[0]
    const cardAspectRatio = getCardAspectRatio()

    return (
      <>
        <div
          style={{
            ...styles.imageCard,
            aspectRatio: cardAspectRatio,
          }}
          onClick={() => handleImageClick(post.id)}
        >
          <img
            src={firstImage.image_url}
            alt={post.product_name}
            style={styles.imageCardBackground}
          />

          {post.shop && (
            <div style={styles.imageCardTopOverlay}>
              <div style={styles.imageCardShopInfo}>
                {post.shop.logo_url && (
                  <div style={styles.imageCardLogoWrapper}>
                    <img
                      src={post.shop.logo_url}
                      style={styles.imageCardLogo}
                      alt={post.shop.chain_name}
                    />
                  </div>
                )}
                <div style={styles.imageCardShopName}>{post.shop.chain_name}</div>
              </div>
            </div>
          )}

          {bargain_user_info.id === post.postable_id && (
            <button
              style={styles.imageCardDeleteButton}
              onClick={(e) => {
                e.stopPropagation()
                void handleDelete()
              }}
            >
              削除
            </button>
          )}

          <div style={styles.imageCardBottomOverlay}>
            <div style={styles.imageCardUserInfo}>
              <span className="icon_font icf_user" style={{ marginRight: '4px', fontSize: '12px' }} />
              <span>{getUserName(post)}</span>
              {getIsStaff(post) && (
                <img src={officialMark} alt="公式" style={styles.imageCardOfficialMark} />
              )}
            </div>
            <PostReactions
              post={post}
              bargain_user_info={bargain_user_info}
              variant="badge"
              onReactionChange={onReactionChange}
            />
          </div>
        </div>
      </>
    )
  }

  const cardAspectRatio = getCardAspectRatio()

  return (
    <div
      style={{
        ...styles.imageCard,
        aspectRatio: cardAspectRatio,
      }}
      onClick={() => {
        handleImageClick(post.id)
      }}
    >
      {bargain_user_info.id === post.postable_id && (
        <button
          style={styles.imageCardDeleteButton}
          onClick={(e) => {
            e.stopPropagation()
            void handleDelete()
          }}
        >
          削除
        </button>
      )}

      <div style={styles.noImageCard}>
        {post.shop && (
          <div style={{ ...styles.imageCardShopInfo, marginBottom: '6px' }}>
            {post.shop.logo_url && (
              <div style={styles.imageCardLogoWrapper}>
                <img
                  src={post.shop.logo_url}
                  style={styles.imageCardLogo}
                  alt={post.shop.chain_name}
                />
              </div>
            )}
            <div
              style={{
                ...styles.imageCardShopName,
                ...styles.noImageCardShopName,
                color: colors['text-secondary'],
                textShadow: 'none',
              }}
            >
              {post.shop.chain_name}
            </div>
          </div>
        )}

        <div
          style={{
            ...styles.imageCardProductName,
            ...styles.noImageCardProductName,
            color: colors['text-default'],
            textShadow: 'none',
            marginBottom: '6px',
          }}
        >
          {post.product_name}
        </div>

        {post.content && <div style={styles.noImageCardDescription}>{post.content}</div>}

        <div style={styles.noImageCardBottom}>
          <div
            style={{
              ...styles.imageCardUserInfo,
              color: colors['text-secondary'],
              textShadow: 'none',
            }}
          >
            <span className="icon_font icf_user" style={{ marginRight: '4px', fontSize: '12px' }} />
            <span>{getUserName(post)}</span>
            {getIsStaff(post) && (
              <img src={officialMark} alt="公式" style={styles.imageCardOfficialMark} />
            )}
          </div>
          <PostReactions
            post={post}
            bargain_user_info={bargain_user_info}
            variant="badge"
            onReactionChange={onReactionChange}
          />
        </div>
      </div>
    </div>
  )
}

export default PostCard
